import { Head, Link, router } from "@inertiajs/react"
import { TrendingUp, Users, BarChart3, Clock, ChevronRight, AlertCircle, RefreshCw } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// Color by pipeline name pattern: article=amber, main=blue, referral=emerald, else=purple
function getPipelineBadgeColor(name) {
  const n = (name || "").toLowerCase()
  if (n.includes("article")) return "bg-amber-100 text-amber-800 hover:bg-amber-100"
  if (n.includes("main")) return "bg-blue-100 text-blue-800 hover:bg-blue-100"
  if (n.includes("referral")) return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
  return "bg-purple-100 text-purple-800 hover:bg-purple-100"
}

function oldestDaysColor(days) {
  if (days == null) return "text-zinc-400"
  if (days >= 30) return "text-red-600 font-semibold"
  if (days >= 14) return "text-amber-600 font-medium"
  return "text-zinc-600"
}

function formatLastSync(isoString) {
  if (!isoString) return null
  const date = new Date(isoString)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function SyncStatusBadge({ coach }) {
  // #556 — an enrolled coach with no GHL location is not a sync failure: the
  // sync skips them. Say what to fix instead of "Sync Error".
  if (!coach.ghlLocationId) {
    return (
      <Badge
        className="bg-amber-100 text-amber-800 hover:bg-amber-100"
        title="Add this coach's CRM Location ID on their People profile to start syncing."
      >
        <AlertCircle className="h-3 w-3 mr-1" />
        Not linked to GHL
      </Badge>
    )
  }
  if (coach.lastSyncError) {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        <AlertCircle className="h-3 w-3 mr-1" />
        Sync Error
      </Badge>
    )
  }
  if (coach.lastSyncedAt) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        Synced
      </Badge>
    )
  }
  return (
    <Badge className="bg-zinc-100 text-zinc-600 hover:bg-zinc-100">
      Pending
    </Badge>
  )
}

export default function MarketingIndex({
  currentUser,
  coaches,
  stats,
  pendingCount,
  pipelineNames,
}) {
  const lastSync = coaches.reduce((latest, coach) => {
    if (!coach.lastSyncedAt) return latest
    if (!latest) return coach.lastSyncedAt
    return coach.lastSyncedAt > latest ? coach.lastSyncedAt : latest
  }, null)

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Marketing - Kingdom Factor Admin" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Marketing</h1>
            <p className="text-zinc-500">
              Pipeline performance across active marketing coaches
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.post("/app/admin/marketing/sync")}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Now
          </Button>
        </div>

        {/* Stat Cards */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Active Coaches</p>
                  <p className="text-2xl font-bold text-zinc-900">
                    {stats.totalCoaches}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50">
                  <BarChart3 className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Total Opportunities</p>
                  <p className="text-2xl font-bold text-zinc-900">
                    {stats.totalOpportunities}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Last Sync</p>
                  <p className="text-lg font-semibold text-zinc-900">
                    {lastSync ? formatLastSync(lastSync) : "Never"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coaches Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {coaches.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Coach</TableHead>
                    <TableHead className="text-zinc-500 text-center">Total</TableHead>
                    <TableHead className="text-zinc-500">Pipeline Breakdown</TableHead>
                    <TableHead className="text-zinc-500 text-center">Avg</TableHead>
                    <TableHead className="text-zinc-500 text-center">Oldest</TableHead>
                    <TableHead className="text-zinc-500">Status</TableHead>
                    <TableHead className="text-zinc-500 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coaches.map((coach) => (
                    <TableRow
                      key={coach.id}
                      className="border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      onClick={() => router.visit(`/app/admin/marketing/${coach.id}`)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-zinc-900">
                            {coach.name || <span className="text-zinc-400 italic">No name</span>}
                          </p>
                          <p className="text-sm text-zinc-500">{coach.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-lg font-semibold text-zinc-900">
                          {coach.totalOpportunities}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {Object.entries(coach.perPipeline || {}).map(([pipelineId, count]) => {
                            if (count === 0) return null
                            const name = pipelineNames[pipelineId] || pipelineId
                            return (
                              <Badge
                                key={pipelineId}
                                className={getPipelineBadgeColor(name)}
                              >
                                {name}: {count}
                              </Badge>
                            )
                          })}
                          {coach.totalOpportunities === 0 && (
                            <span className="text-sm text-zinc-400">No opportunities</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-zinc-600">
                          {coach.avgDays != null ? `${coach.avgDays}d` : "--"}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={oldestDaysColor(coach.oldestDays)}>
                          {coach.oldestDays != null ? `${coach.oldestDays}d` : "--"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <SyncStatusBadge coach={coach} />
                      </TableCell>
                      <TableCell>
                        <Link
                          href={`/app/admin/marketing/${coach.id}`}
                          className="text-zinc-400 hover:text-zinc-600"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No enrolled coaches</p>
                <p className="text-sm">
                  Enroll coaches in the marketing program from their People profile to start tracking pipeline data.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
